import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import authBgMobile from "@/assets/auth-background-768.webp";
import authBgTablet from "@/assets/auth-background-1280.webp";
import authBgDesktop from "@/assets/auth-background-1920.webp";

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Honeypot fields - bots will fill these, real users won't see them
  const [honeypotWebsite, setHoneypotWebsite] = useState("");
  const [honeypotPhone, setHoneypotPhone] = useState("");
  const [resetHoneypot, setResetHoneypot] = useState("");
  
  const navigate = useNavigate();
  const { user } = useAuth();

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'robots';
    metaTag.content = 'noindex, nofollow';
    document.head.appendChild(metaTag);

    return () => {
      document.head.removeChild(metaTag);
    };
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const checkRateLimit = async (attemptType: 'signin' | 'signup' | 'reset', action: 'check' | 'record' = 'check'): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('check-auth-rate-limit', {
        body: { attemptType, action }
      });

      if (error) {
        console.error('Rate limit check failed:', error);
        // Fail open - allow the attempt if rate limiting fails
        return true;
      }

      if (!data.allowed) {
        const minutes = Math.ceil(data.retryAfter / 60);
        setRateLimitError(`Too many attempts. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`);
        return false;
      }

      setRateLimitError(null);
      return true;
    } catch (err) {
      console.error('Rate limit check error:', err);
      // Fail open on network errors
      return true;
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check honeypot fields - if filled, silently "succeed" (bot trap)
    if (honeypotWebsite || honeypotPhone) {
      // Fake success to fool bots
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success("Signed in successfully!");
      setLoading(false);
      return;
    }
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    setRateLimitError(null);

    try {
      // Check rate limit before attempting sign in
      const allowed = await checkRateLimit('signin');
      if (!allowed) {
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      toast.success("Signed in successfully!");
      navigate("/dashboard");
    } catch (error: any) {
      await checkRateLimit('signin', 'record');
      toast.error(error.message || "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check honeypot field for reset form
    if (resetHoneypot) {
      // Fake success to fool bots
      setResetLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success("Password reset instructions sent to your email!");
      setResetDialogOpen(false);
      setResetEmail("");
      setResetLoading(false);
      return;
    }
    
    if (!resetEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setResetLoading(true);
    setRateLimitError(null);

    try {
      // Check rate limit before attempting reset
      const allowed = await checkRateLimit('reset');
      if (!allowed) {
        setResetLoading(false);
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;
      
      toast.success("Password reset instructions sent to your email!");
      setResetDialogOpen(false);
      setResetEmail("");
    } catch (error: any) {
      await checkRateLimit('reset', 'record');
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setResetLoading(false);
    }
  };

  // Honeypot field styles - positioned off-screen, invisible to users
  const honeypotStyle: React.CSSProperties = {
    position: 'absolute',
    left: '-9999px',
    opacity: 0,
    pointerEvents: 'none',
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      <picture className="absolute inset-0 -z-10">
        <source media="(max-width: 768px)" srcSet={authBgMobile} type="image/webp" />
        <source media="(max-width: 1280px)" srcSet={authBgTablet} type="image/webp" />
        <img
          src={authBgDesktop}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
      </picture>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm -z-10" />
      
      <Card className="w-full max-w-md shadow-xl relative z-10">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <Logo variant="light" className="h-32" />
          </div>
          <CardTitle className="text-2xl">Welcome Back</CardTitle>
          <CardDescription>Login to complete your tasks</CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            {/* Honeypot fields - invisible to users, bots will fill them */}
            <div style={honeypotStyle} aria-hidden="true">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypotWebsite}
                onChange={(e) => setHoneypotWebsite(e.target.value)}
              />
            </div>
            <div style={honeypotStyle} aria-hidden="true">
              <Label htmlFor="phone_confirm">Phone Confirmation</Label>
              <Input
                id="phone_confirm"
                name="phone_confirm"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypotPhone}
                onChange={(e) => setHoneypotPhone(e.target.value)}
              />
            </div>

            {rateLimitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{rateLimitError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={loading || !!rateLimitError}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="link" className="w-full text-sm" type="button">
                  Forgot Password?
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                  <DialogDescription>
                    Enter your email address and we'll send you instructions to reset your password.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {/* Honeypot field for reset form */}
                  <div style={honeypotStyle} aria-hidden="true">
                    <Label htmlFor="reset_confirm">Confirmation Code</Label>
                    <Input
                      id="reset_confirm"
                      name="reset_confirm"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={resetHoneypot}
                      onChange={(e) => setResetHoneypot(e.target.value)}
                    />
                  </div>

                  {rateLimitError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{rateLimitError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="your@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={resetLoading || !!rateLimitError}>
                    {resetLoading ? "Sending..." : "Send Reset Instructions"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
